import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { LogOut, Inbox, CalendarDays, MessageSquare, Plus, Trash2, Check, X, Sparkles, Star } from "lucide-react";

const tabs = [
  { id: "bookings", label: "Bookings", icon: Inbox },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "questionnaires", label: "Jacket briefs", icon: Sparkles },
  { id: "workshops", label: "Workshops", icon: CalendarDays },
  { id: "drops", label: "Drops", icon: Star },
];

const emptyWorkshop = {
  title: "", description: "", date: "", time: "", location: "Hypha Studio, Walter Burke Way, Chatham, ME4 4RQ",
  capacity: 10, price: 0, price_note: "", notes: "", age_group: "All ages from 8", workshop_type: "denim", is_active: true,
};

const emptyDrop = {
  title: "", subtitle: "", description: "", image_url: "", instagram_url: "",
  tag: "NEW DROP", is_featured: false, is_active: true, sort_order: 0,
};

const FIELD_LABEL = "text-[10px] uppercase tracking-[0.25em] text-gold font-bold";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyWorkshop);
  const [editingDrop, setEditingDrop] = useState(null);
  const [dropForm, setDropForm] = useState(emptyDrop);

  const loadAll = useCallback(async () => {
    try {
      const [s, b, m, w, q, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/messages"),
        api.get("/admin/workshops"),
        api.get("/admin/jacket-questionnaires"),
        api.get("/admin/drops"),
      ]);
      setStats(s.data); setBookings(b.data); setMessages(m.data); setWorkshops(w.data); setQuestionnaires(q.data); setDrops(d.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doLogout = () => { logout(); nav("/admin/login"); };

  const updateBooking = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success(`Booking ${status}`);
    loadAll();
  };
  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    await api.delete(`/admin/bookings/${id}`); loadAll();
  };
  const markRead = async (id) => { await api.patch(`/admin/messages/${id}`); loadAll(); };
  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/messages/${id}`); loadAll();
  };

  const startEdit = (w) => {
    if (w === "new") { setForm(emptyWorkshop); setEditing("new"); }
    else { setForm({ ...w }); setEditing(w.id); }
  };
  const saveWorkshop = async () => {
    try {
      const payload = { ...form, capacity: Number(form.capacity), price: Number(form.price) };
      if (editing === "new") await api.post("/admin/workshops", payload);
      else await api.patch(`/admin/workshops/${editing}`, payload);
      toast.success("Workshop saved"); setEditing(null); loadAll();
    } catch (e) { toast.error("Save failed"); }
  };
  const deleteWorkshop = async (id) => {
    if (!window.confirm("Delete this workshop?")) return;
    await api.delete(`/admin/workshops/${id}`); loadAll();
  };

  const startDropEdit = (d) => {
    if (d === "new") { setDropForm(emptyDrop); setEditingDrop("new"); }
    else { setDropForm({ ...d }); setEditingDrop(d.id); }
  };
  const saveDrop = async () => {
    try {
      const payload = { ...dropForm, sort_order: Number(dropForm.sort_order) || 0 };
      if (editingDrop === "new") await api.post("/admin/drops", payload);
      else await api.patch(`/admin/drops/${editingDrop}`, payload);
      toast.success("Drop saved"); setEditingDrop(null); loadAll();
    } catch (e) { toast.error("Save failed"); }
  };
  const deleteDrop = async (id) => {
    if (!window.confirm("Delete this drop?")) return;
    await api.delete(`/admin/drops/${id}`); loadAll();
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink">
      <header className="bg-coal border-b-2 border-white/10">
        <div className="container-x flex items-center justify-between py-5">
          <div>
            <p className="overline">Studio control</p>
            <h1 className="font-display text-3xl uppercase text-white">Hello {user?.name || "Lisa"}</h1>
          </div>
          <button data-testid="admin-logout-btn" onClick={doLogout} className="btn-secondary text-xs">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <div className="container-x py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-10">
          {[
            { l: "Confirmed", v: stats.confirmed_bookings ?? "—", testid: "stat-confirmed", accent: "rose" },
            { l: "Total bookings", v: stats.total_bookings ?? "—", testid: "stat-total-bookings" },
            { l: "Messages", v: stats.total_messages ?? "—", testid: "stat-messages" },
            { l: "Unread", v: stats.unread_messages ?? "—", testid: "stat-unread-messages", accent: "gold" },
            { l: "Jacket briefs", v: stats.total_questionnaires ?? "—", testid: "stat-jacket-briefs" },
            { l: "Workshops", v: stats.active_workshops ?? "—", testid: "stat-active-workshops" },
            { l: "Drops", v: stats.total_drops ?? "—", testid: "stat-drops" },
          ].map((s) => (
            <div
              key={s.l}
              data-testid={s.testid}
              className={`border-2 p-4 ${
                s.accent === "rose" ? "bg-rose border-white" :
                s.accent === "gold" ? "bg-gold border-ink text-ink" :
                "bg-coal border-white/30"
              }`}
            >
              <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${s.accent === "gold" ? "text-ink/80" : s.accent === "rose" ? "text-white/90" : "text-white/60"}`}>{s.l}</div>
              <div className={`mt-2 font-display text-4xl ${s.accent === "gold" ? "text-ink" : "text-white"}`}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b-2 border-white/10 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold border-b-2 transition ${
                tab === t.id ? "border-rose text-rose" : "border-transparent text-white/60 hover:text-white"
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {/* Bookings */}
        {tab === "bookings" && (
          <div data-testid="bookings-panel" className="space-y-3">
            {bookings.length === 0 && <p className="text-white/60">No bookings yet.</p>}
            {bookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-coal border-2 border-white/15 p-5 flex flex-wrap gap-4 items-start justify-between">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display text-2xl text-white">{b.parent_name || b.name}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                      b.status === "confirmed" ? "bg-lime text-ink" :
                      b.status === "contacted" ? "bg-white/15 text-white" :
                      "bg-rose text-white"
                    }`}>{b.status}</span>
                    {b.deposit_paid && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-gold text-ink">£{Number(b.deposit_amount).toFixed(0)} paid</span>
                    )}
                  </div>
                  <div className="text-sm text-white/70 mt-1">
                    {b.email} · {b.phone || "no phone"}{b.emergency_contact && ` · 🆘 ${b.emergency_contact}`}
                  </div>
                  {b.child_name && (
                    <div className="text-sm text-white mt-1">
                      Attendee: <span className="font-bold text-rose">{b.child_name}</span>{b.child_age && ` · age ${b.child_age}`}
                    </div>
                  )}
                  <div className="text-sm text-white/70 mt-1">
                    {b.workshop_type} · {b.age_group} · {b.participants} attendee(s) · {b.preferred_date || "no date"}
                  </div>
                  {b.allergies && <p className="mt-2 text-xs text-rose bg-rose/10 border border-rose px-3 py-2">⚠️ {b.allergies}</p>}
                  {b.message && <p className="mt-2 text-sm text-white/85 bg-ink/60 p-3 border-l-2 border-rose">{b.message}</p>}
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{new Date(b.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => updateBooking(b.id, "contacted")} className="btn-ghost text-xs">Contacted</button>
                  <button onClick={() => updateBooking(b.id, "confirmed")} className="btn-primary text-xs">Confirm</button>
                  <button onClick={() => deleteBooking(b.id)} className="text-white/60 hover:text-rose p-2"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Messages */}
        {tab === "messages" && (
          <div data-testid="messages-panel" className="space-y-3">
            {messages.length === 0 && <p className="text-white/60">No messages yet.</p>}
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-row-${m.id}`} className={`p-5 border-2 flex flex-wrap gap-4 justify-between ${m.read ? "bg-coal border-white/15" : "bg-rose/10 border-rose"}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-white">{m.name}</span>
                    {!m.read && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-rose text-white">New</span>}
                  </div>
                  <div className="text-sm text-white/70 mt-1">{m.email}{m.subject && ` · ${m.subject}`}</div>
                  <p className="mt-3 text-sm text-white/85 whitespace-pre-wrap">{m.message}</p>
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{new Date(m.created_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  {!m.read && <button onClick={() => markRead(m.id)} className="btn-ghost text-xs"><Check size={14} /> Read</button>}
                  <button onClick={() => deleteMessage(m.id)} className="text-white/60 hover:text-rose p-2"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Jacket briefs */}
        {tab === "questionnaires" && (
          <div data-testid="questionnaires-panel" className="space-y-3">
            {questionnaires.length === 0 && <p className="text-white/60">No jacket briefs yet.</p>}
            {questionnaires.map((q) => (
              <div key={q.id} data-testid={`questionnaire-row-${q.id}`} className={`p-5 border-2 ${q.read ? "bg-coal border-white/15" : "bg-rose/10 border-rose"}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-2xl text-white">{q.child_name}</span>
                  <span className="text-xs text-gold uppercase tracking-wider">age {q.child_age}</span>
                  {!q.read && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-rose text-white">New</span>}
                </div>
                {(q.parent_name || q.parent_email) && (
                  <div className="text-sm text-white/70 mt-1">{q.parent_name}{q.parent_email && ` · ${q.parent_email}`}</div>
                )}
                <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                  {q.favourite_colours && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Colours</span><div className="text-white">{q.favourite_colours}</div></div>}
                  {q.hobbies && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Hobbies</span><div className="text-white">{q.hobbies}</div></div>}
                  {q.characters && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Themes</span><div className="text-white">{q.characters}</div></div>}
                  {q.style_preference?.length > 0 && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Style</span><div className="text-white">{q.style_preference.join(", ")}</div></div>}
                  {q.layout_preference?.length > 0 && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Layout</span><div className="text-white">{q.layout_preference.join(", ")}</div></div>}
                  {q.rhinestones && q.rhinestones !== "none" && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Sparkle</span><div className="text-white">{q.rhinestones}</div></div>}
                  {q.favourite_quotes && <div className="sm:col-span-2"><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Quotes</span><div className="text-white">{q.favourite_quotes}</div></div>}
                  {q.names_initials && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Names/initials</span><div className="text-white">{q.names_initials}</div></div>}
                  {q.jacket_colour && <div><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Jacket</span><div className="text-white">{q.jacket_colour}</div></div>}
                  {q.must_include && <div className="sm:col-span-2"><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Do include</span><div className="text-white whitespace-pre-wrap">{q.must_include}</div></div>}
                  {q.must_avoid && <div className="sm:col-span-2"><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Do NOT include</span><div className="text-white whitespace-pre-wrap">{q.must_avoid}</div></div>}
                  {q.inspiration_urls && <div className="sm:col-span-2"><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Inspiration</span><div className="text-white whitespace-pre-wrap break-all">{q.inspiration_urls}</div></div>}
                  {q.notes && <div className="sm:col-span-2"><span className="text-gold text-[10px] uppercase tracking-wider font-bold">Notes</span><div className="text-white whitespace-pre-wrap">{q.notes}</div></div>}
                </div>
                <div className="flex gap-2 mt-4 justify-end">
                  {!q.read && <button onClick={async () => { await api.patch(`/admin/jacket-questionnaires/${q.id}`); loadAll(); }} className="btn-ghost text-xs"><Check size={14} /> Mark read</button>}
                  <button onClick={async () => { if (window.confirm("Delete this brief?")) { await api.delete(`/admin/jacket-questionnaires/${q.id}`); loadAll(); } }} className="text-white/60 hover:text-rose p-2"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Workshops */}
        {tab === "workshops" && (
          <div data-testid="workshops-panel">
            <div className="flex justify-end mb-5">
              <button data-testid="add-workshop-btn" onClick={() => startEdit("new")} className="btn-accent text-xs"><Plus size={14} /> New workshop</button>
            </div>

            {editing && (
              <div data-testid="workshop-editor" className="bg-coal border-2 border-rose p-6 mb-6 grid sm:grid-cols-2 gap-4">
                {[
                  ["title", "Title"], ["date", "Date"], ["time", "Time"], ["location", "Location"],
                  ["age_group", "Age group"], ["workshop_type", "Type"],
                ].map(([k, l]) => (
                  <label key={k} className="block">
                    <span className={FIELD_LABEL}>{l}</span>
                    <input value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} className="input-brutal mt-2" />
                  </label>
                ))}
                <label className="block"><span className={FIELD_LABEL}>Capacity</span>
                  <input type="number" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} className="input-brutal mt-2" />
                </label>
                <label className="block"><span className={FIELD_LABEL}>Price (£)</span>
                  <input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="input-brutal mt-2" />
                </label>
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Price note</span>
                  <input value={form.price_note || ""} onChange={e => setForm({ ...form, price_note: e.target.value })} className="input-brutal mt-2" placeholder="e.g. £50 for 4 weeks · or £15 per session" />
                </label>
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Notes</span>
                  <input value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} className="input-brutal mt-2" placeholder="e.g. Participants bring their own denim item" />
                </label>
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Description</span>
                  <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="input-brutal mt-2 resize-none" />
                </label>
                <label className="flex items-center gap-2 sm:col-span-2 text-sm text-white/80">
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="accent-rose" />
                  Active (visible to public)
                </label>
                <div className="sm:col-span-2 flex gap-3 justify-end">
                  <button onClick={() => setEditing(null)} className="btn-ghost text-xs"><X size={14} /> Cancel</button>
                  <button data-testid="save-workshop-btn" onClick={saveWorkshop} className="btn-accent text-xs">Save</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {workshops.map((w) => (
                <div key={w.id} data-testid={`workshop-row-${w.id}`} className="bg-coal border-2 border-white/15 p-5 flex flex-wrap gap-4 justify-between">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-2xl text-white">{w.title}</span>
                      {!w.is_active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-white/15 text-white/70">Hidden</span>}
                    </div>
                    <div className="text-sm text-white/70 mt-1">{w.date} · {w.time} · {w.location}</div>
                    <div className="text-sm text-white/70">{w.age_group} · £{w.price} · {w.workshop_type}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(w)} className="btn-ghost text-xs">Edit</button>
                    <button onClick={() => deleteWorkshop(w.id)} className="text-white/60 hover:text-rose p-2"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Drops */}
        {tab === "drops" && (
          <div data-testid="drops-panel">
            <div className="flex justify-end mb-5">
              <button data-testid="add-drop-btn" onClick={() => startDropEdit("new")} className="btn-accent text-xs"><Plus size={14} /> New drop</button>
            </div>

            {editingDrop && (
              <div data-testid="drop-editor" className="bg-coal border-2 border-rose p-6 mb-6 grid sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Image URL *</span>
                  <input required value={dropForm.image_url} onChange={e => setDropForm({ ...dropForm, image_url: e.target.value })} className="input-brutal mt-2" placeholder="https://…" />
                </label>
                {dropForm.image_url && (
                  <div className="sm:col-span-2 aspect-video overflow-hidden border-2 border-white/20 max-h-64">
                    <img src={dropForm.image_url} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <label className="block"><span className={FIELD_LABEL}>Title *</span>
                  <input required value={dropForm.title} onChange={e => setDropForm({ ...dropForm, title: e.target.value })} className="input-brutal mt-2" />
                </label>
                <label className="block"><span className={FIELD_LABEL}>Subtitle</span>
                  <input value={dropForm.subtitle || ""} onChange={e => setDropForm({ ...dropForm, subtitle: e.target.value })} className="input-brutal mt-2" placeholder="e.g. Sunsets, stars & favourite words" />
                </label>
                <label className="block"><span className={FIELD_LABEL}>Tag</span>
                  <input value={dropForm.tag || ""} onChange={e => setDropForm({ ...dropForm, tag: e.target.value })} className="input-brutal mt-2" placeholder="e.g. NEW DROP, ONE-OFF, SIGNATURE" />
                </label>
                <label className="block"><span className={FIELD_LABEL}>Sort order (higher = earlier)</span>
                  <input type="number" value={dropForm.sort_order} onChange={e => setDropForm({ ...dropForm, sort_order: e.target.value })} className="input-brutal mt-2" />
                </label>
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Instagram / link URL</span>
                  <input value={dropForm.instagram_url || ""} onChange={e => setDropForm({ ...dropForm, instagram_url: e.target.value })} className="input-brutal mt-2" placeholder="https://instagram.com/p/…" />
                </label>
                <label className="block sm:col-span-2"><span className={FIELD_LABEL}>Description</span>
                  <textarea rows={3} value={dropForm.description || ""} onChange={e => setDropForm({ ...dropForm, description: e.target.value })} className="input-brutal mt-2 resize-none" />
                </label>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={dropForm.is_featured} onChange={e => setDropForm({ ...dropForm, is_featured: e.target.checked })} className="accent-rose" />
                  Featured (large hero)
                </label>
                <label className="flex items-center gap-2 text-sm text-white/80">
                  <input type="checkbox" checked={dropForm.is_active} onChange={e => setDropForm({ ...dropForm, is_active: e.target.checked })} className="accent-rose" />
                  Active (visible to public)
                </label>
                <div className="sm:col-span-2 flex gap-3 justify-end">
                  <button onClick={() => setEditingDrop(null)} className="btn-ghost text-xs"><X size={14} /> Cancel</button>
                  <button data-testid="save-drop-btn" onClick={saveDrop} className="btn-accent text-xs">Save drop</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {drops.length === 0 && <p className="text-white/60">No drops yet. Click "New drop" to add the first piece.</p>}
              {drops.map((d) => (
                <div key={d.id} data-testid={`drop-row-${d.id}`} className="bg-coal border-2 border-white/15 p-4 flex flex-wrap gap-4 justify-between items-start">
                  <div className="flex gap-4 flex-1 min-w-[260px]">
                    {d.image_url && <img src={d.image_url} alt={d.title} className="w-20 h-20 object-cover border-2 border-white/20 flex-shrink-0" />}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display text-xl text-white">{d.title}</span>
                        {d.is_featured && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-rose text-white">Featured</span>}
                        {!d.is_active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-white/15 text-white/70">Hidden</span>}
                      </div>
                      {d.subtitle && <div className="text-sm text-gold mt-0.5">{d.subtitle}</div>}
                      {d.description && <div className="text-sm text-white/70 mt-1 line-clamp-2">{d.description}</div>}
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mt-2">tag: {d.tag} · order: {d.sort_order}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startDropEdit(d)} className="btn-ghost text-xs">Edit</button>
                    <button onClick={() => deleteDrop(d.id)} className="text-white/60 hover:text-rose p-2"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
